import set from "@rill/set";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import i18n from "../../../i18n";
import { checkOrganizationSectionPermission } from "../../../middleware/ensure-coach-permissions";
import Select from "../../../parts/inputs/select";
import SettingsNav from "../../../parts/settings-nav";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { buildTierBreakdown } from "../../../utils/perform-util";
import { SettingsBackHandler } from "../../coaching-portal/settings/components/SettingsBackHandler";

const enumerateMonths = (startDate, momentTZ) => {
  const dateEnd = momentTZ().startOf("month");
  let dateStart = momentTZ(startDate).startOf("month");
  const months = [];

  while (!dateStart.isAfter(dateEnd)) {
    months.push({
      label: dateStart.format("MMMM YYYY"),
      month: dateStart.month() + 1,
      year: dateStart.year(),
    });
    dateStart = dateStart.add(1, "month");
  }

  return months;
};

const PerformInvoicePage = ({ months, selectedMonthLabel }) => {
  const [selectedMonth, setSelectedMonth] = useState("");
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    submit("/actions/refresh", {
      method: "GET",
      body: { date: value },
    });
  };

  const { performBillingSummary } = locals;
  const defaultMonth = months[months.length - 1];

  const billingSummary = performBillingSummary?.billingSummary ?? null;
  const memberCount = billingSummary?.billing?.memberCount ?? 0;
  const isEnterprise = billingSummary?.billing?.tier === "enterprise";
  const totalAmount = billingSummary?.billing?.amountInDollars ?? 0;
  // The coupon is applied to the Stripe invoice, not to the tier calculation,
  // so the charged total can be lower than the tier rows add up to.
  const discountAmount = billingSummary?.billing?.discountAmount ?? 0;
  const netAmount = billingSummary?.billing?.netAmount ?? totalAmount;
  const tierBreakdown = isEnterprise ? [] : buildTierBreakdown(memberCount);

  const receipts = locals?.performReceipts?.receipts || undefined;
  const downloadReceiptUrl =
    (receipts && (receipts[0]?.receipt_url || receipts[1]?.receipt_url)) ||
    null;

  return (
    <main className="settings">
      <SettingsNav classNames="mobile-hidden" />

      <section className="perform-invoice">
        <h3 className="force-mobile-hidden">{t("performInvoice.title")}</h3>
        <SettingsBackHandler title={t("performInvoice.title")} />

        <form id="perform-invoice-form" action="#_" method="GET" data-noreset>
          <header>
            <hgroup>
              <Select
                name="date"
                options={[...months].reverse().map((m) => ({
                  text: m.label,
                  value: `${m.month}-${m.year}`,
                }))}
                onChange={handleMonthChange}
                title={t("performInvoice.billingMonth")}
                value={
                  selectedMonth || `${defaultMonth.month}-${defaultMonth.year}`
                }
              />
            </hgroup>
            {downloadReceiptUrl && (
              <button
                data-button-type="primary"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(downloadReceiptUrl, "_blank");
                }}
                type="button"
              >
                {t("performInvoice.downloadReceipt")}
              </button>
            )}
          </header>

          {billingSummary ? (
            <>
              <div className="perform-invoice-summary-cards">
                <div className="summary-card">
                  <div className="summary-card-content">
                    <p className="summary-card-label">
                      {t("performInvoice.totalActiveMembers")}
                    </p>
                    <p className="summary-card-value">{memberCount}</p>
                  </div>
                  <svg
                    className="summary-card-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div className="summary-card">
                  <div className="summary-card-content">
                    <p className="summary-card-label">
                      {t("performInvoice.billingPeriod")}
                    </p>
                    <p className="summary-card-value">
                      {t("performInvoice.billingPeriodMonthly")}
                    </p>
                  </div>
                  <svg
                    className="summary-card-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>

              <h4 className="breakdown-title">
                {t("performInvoice.priceBreakdownTitle", {
                  month: selectedMonthLabel,
                })}
              </h4>

              {isEnterprise ? (
                <div className="enterprise-billing-notice">
                  <p className="s1">{t("performInvoice.enterpriseNotice")}</p>
                </div>
              ) : (
                <section role="table-wrapper" data-borders-inside>
                  <table
                    role="report-table"
                    className="perform-breakdown-table"
                  >
                    <thead role="table-header">
                      <tr>
                        <th>{t("performInvoice.lineItem")}</th>
                        <th className="text-center">
                          {t("performInvoice.activeMembers")}
                        </th>
                        <th>{t("performInvoice.priceDescription")}</th>
                        <th className="text-center">
                          {t("performInvoice.amount")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tierBreakdown.map((row) => (
                        <tr key={row.label}>
                          <td>
                            <strong>{row.label}</strong>
                            <br />
                            <span className="tier-range">({row.range})</span>
                          </td>
                          <td className="text-center">{row.members}</td>
                          <td>
                            <strong>{row.rule}</strong>
                            {row.ruleDetail && (
                              <>
                                <br />
                                <span className="rule-detail">
                                  {row.ruleDetail}
                                </span>
                              </>
                            )}
                          </td>
                          <td className="text-center">
                            <strong>${row.price.toFixed(2)}</strong>
                          </td>
                        </tr>
                      ))}
                      <tr className="total-row">
                        <td>
                          <strong>{t("performInvoice.totalCost")}</strong>
                        </td>
                        <td className="text-center">
                          {t("performInvoice.membersTotal", {
                            count: memberCount,
                          })}
                        </td>
                        <td></td>
                        <td className="text-center total-amount">
                          ${totalAmount.toFixed(2)}
                        </td>
                      </tr>
                      {discountAmount > 0 && (
                        <>
                          <tr className="discount-row">
                            <td>
                              <strong>{t("performInvoice.discount")}</strong>
                            </td>
                            <td></td>
                            <td></td>
                            <td className="text-center discount-amount">
                              -${discountAmount.toFixed(2)}
                            </td>
                          </tr>
                          <tr className="total-row">
                            <td>
                              <strong>
                                {t("performInvoice.totalCharged")}
                              </strong>
                            </td>
                            <td></td>
                            <td></td>
                            <td className="text-center total-amount">
                              ${netAmount.toFixed(2)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          ) : (
            <p className="s1">{t("performInvoice.noReportYet")}</p>
          )}
        </form>

        {/* <form
					action="/dashboard/settings/perform-invoice"
					method="POST"
					className="form-cancel-subscription"
					id="cancel-perform-subscription-form"
				>
					<button
						data-button-type="secondary"
						data-action="cancel"
						type="submit"
						form="cancel-perform-subscription-form"
					>
						Cancel Subscription
					</button>
				</form> */}
      </section>
    </main>
  );
};

const PerformInvoice = (app) => {
  app
    .get(
      "/perform-invoice",
      set("heading", i18n.t("dashboard:performInvoice.title")),
      (ctx, next) =>
        checkOrganizationSectionPermission(
          ctx,
          next,
          "settings",
          "/coaching-portal/settings",
          ["Owner"]
        ),
      async ({ res, req, locals, momentTZ, load }) => {
        const { query } = req;
        const { activeUserToRequest } = locals;

        // Determine start date from earliest performClient or fallback to current month
        let billingStartDate = momentTZ();
        const performClients = activeUserToRequest.performClients || [];
        if (performClients.length > 0) {
          const earliest = performClients.reduce((min, client) => {
            const addedAt = new Date(client.addedAt);
            return Math.min(addedAt, min);
          }, new Date());
          billingStartDate = momentTZ(earliest);
        } else if (activeUserToRequest.performBilling?.currentPeriodStart) {
          billingStartDate = momentTZ(
            activeUserToRequest.performBilling.currentPeriodStart
          );
        }

        const months = enumerateMonths(billingStartDate, momentTZ);

        const today = momentTZ();
        const defaultDate = `${today.month() + 1}-${today.year()}`;
        const date = query.date
          ? query.date.split("-")
          : defaultDate.split("-");
        const [month, year] = date;

        // Calculate period dates for the API call
        const periodStart = moment()
          .year(year)
          .month(month - 1)
          .startOf("month")
          .toISOString();
        const periodEnd = moment()
          .year(year)
          .month(month - 1)
          .endOf("month")
          .toISOString();

        // Load PERFORM receipts for the selected period
        const startDateUnix = moment(periodStart).unix();
        const endDateUnix = moment(periodEnd).unix();
        await Promise.all([
          load("performBillingSummary", periodStart, periodEnd),
          load("performReceipts", startDateUnix, endDateUnix),
        ]);

        const isCurrentMonth = Number(today.month() + 1) === Number(month);

        const selectedMonthLabel = moment()
          .year(year)
          .month(month - 1)
          .format("MMMM YYYY");

        res.body = (
          <PerformInvoicePage
            months={months}
            isCurrentMonth={isCurrentMonth}
            selectedMonthLabel={selectedMonthLabel}
          />
        );
      }
    )
    .post(
      "/perform-invoice",
      (ctx, next) =>
        checkOrganizationSectionPermission(
          ctx,
          next,
          "settings",
          "/coaching-portal/settings",
          ["Owner"]
        ),
      async ({ res, api, flash }) => {
        if (
          process.browser &&
          !confirm(i18n.t("dashboard:performInvoice.confirmCancelSubscription"))
        ) {
          return res.backDontScroll();
        }

        await api("stripe-cancel-perform-subscription", {
          method: "PUT",
          body: {},
        });
        flash(
          "success",
          i18n.t("dashboard:performInvoice.cancelSubscriptionSuccess")
        );

        res.redirect("/coaching-portal/settings/your-products");
      }
    );
};

export default PerformInvoice;
